/**
 * What the list-review panel should be showing, whichever mode produced the
 * list (specification §11.2, §16).
 *
 * Deliberately separate from the import panel, which is about **the file that
 * was just read** — its sheet, its delimiter, the rows it contained. This is
 * about **the list that is about to be sent**, and it looks the same whether
 * that list was typed, pasted in two blocks or imported.
 *
 * Two things it decides:
 *
 * **Which rows will not be sent, and why.** Shown, never dropped — a list that
 * silently shrank from 150 to 148 is the worst possible outcome (§59), and
 * these rows do not block anything. Fixing three bad numbers out of ten
 * thousand is the administrator's call, not the panel's.
 *
 * **Whether the repeated numbers have been decided about.** This one *does*
 * block. Both answers are wrong sometimes — a list pasted twice wants the
 * first row, a corrected row appended at the bottom wants the last — so
 * guessing would silently send the wrong promo code to a real customer.
 */

import {
  checkCampaign,
  type CampaignCheck,
  type CampaignProblem,
} from '../domain/campaign-check.ts';
import type { CampaignInput } from '../domain/campaign-input.ts';
import type { DuplicateGroup, Recipient, RecipientProblem } from '../domain/recipients.ts';
import type { ValidationTotals } from '../domain/validation.ts';
import { previewSlice } from './preview.ts';

export type ProblemRow =
  | {
      readonly kind: 'problem';
      readonly line: number;
      readonly phone: string;
      readonly code: string;
      readonly problem: RecipientProblem;
      readonly params: Readonly<Record<string, number>>;
    }
  | { readonly kind: 'gap'; readonly hidden: number };

export interface DuplicateChoice {
  readonly line: number;
  readonly phone: string;
  readonly code: string;
  /** The row that will be sent under the current resolution. */
  readonly kept: boolean;
}

export type DuplicateRow =
  | { readonly kind: 'group'; readonly index: number; readonly rows: readonly DuplicateChoice[] }
  | { readonly kind: 'gap'; readonly hidden: number };

export interface ListView {
  readonly totals: ValidationTotals;
  readonly problems: readonly ProblemRow[];
  readonly duplicates: readonly DuplicateRow[];
  /** How many numbers are repeated, before any windowing. */
  readonly duplicateCount: number;
  /** Whether the repeated-number question has been answered. */
  readonly decided: boolean;
  readonly findings: readonly CampaignProblem[];
  /** Whether Continue may be pressed, as far as the list is concerned. */
  readonly ready: boolean;
}

export interface ListViewOptions {
  readonly maxRows: number;
}

/**
 * Callers that already have a check — the screen computes one per redraw and
 * hands it to both panels — pass it in rather than paying for a second one over
 * ten thousand rows.
 */
export function listView(
  input: CampaignInput,
  options: ListViewOptions,
  check: CampaignCheck = checkCampaign(input, { maxRows: options.maxRows }),
): ListView {
  const { outcome, decided } = check;
  // The verdict is `campaign-check`'s; this module only decides how to show it.
  const findings = check.problems.filter((problem) => problem.name.startsWith('list-'));

  return {
    totals: outcome.totals,
    problems: problemRows(outcome.recipients),
    duplicates: duplicateRows(outcome.recipients, outcome.duplicates),
    duplicateCount: outcome.duplicates.length,
    decided,
    findings,
    ready: findings.length === 0 && outcome.totals.valid > 0,
  };
}

/** The rows that cannot be sent. Duplicates are excluded: they have their own panel. */
function problemRows(recipients: readonly Recipient[]): readonly ProblemRow[] {
  const rows: ProblemRow[] = recipients
    .filter((row) => row.status === 'invalid' && row.problem !== undefined)
    .map((row) => ({
      kind: 'problem' as const,
      line: row.line,
      // The raw text, always: this row exists because something in it has to be
      // corrected, and the corrected thing is what was written.
      phone: row.rawPhone,
      code: row.promoCode,
      problem: row.problem as RecipientProblem,
      params: {},
    }));

  return windowed(rows);
}

function duplicateRows(
  recipients: readonly Recipient[],
  groups: readonly DuplicateGroup[],
): readonly DuplicateRow[] {
  const byLine = new Map(recipients.map((row) => [row.line, row]));

  const rows: DuplicateRow[] = groups.map((group, index) => ({
    kind: 'group' as const,
    index,
    rows: group.lines.map((line) => ({
      line,
      phone: byLine.get(line)?.phone ?? group.phone,
      code: byLine.get(line)?.promoCode ?? '',
      kept: line === group.kept,
    })),
  }));

  return windowed(rows);
}

function windowed<T extends { readonly kind: string }>(
  rows: readonly T[],
): readonly (T | { readonly kind: 'gap'; readonly hidden: number })[] {
  const slice = previewSlice(rows);
  if (slice.hidden === 0) return slice.head;

  return [...slice.head, { kind: 'gap' as const, hidden: slice.hidden }, ...slice.tail];
}
